import { apiClient } from './apiClient';
import type {
  BooksellerAuthConfigAdminDto,
  CreateBooksellerAuthConfigDto,
  UpdateBooksellerAuthConfigDto,
} from '../types/booksellerAuthConfig';

const BASE = '/api/app/bookseller-auth-configs';

interface AbpListResponse<T> {
  items: T[];
  totalCount: number;
}

// The LIST endpoint returns WithNavigationPropertiesDto — a wrapper around the
// actual config DTO. We unwrap to return the flat config to callers.
interface BooksellerAuthConfigWithNavigationPropertiesDto {
  booksellerAuthConfig: BooksellerAuthConfigAdminDto;
  bookseller: unknown;
}

export const booksellerAuthConfigsService = {
  /**
   * Returns the first auth config for the given bookseller, or null if none exists.
   * The schema allows 1:many but by convention we expect 0 or 1; runtime (ConfigurableEntitlementAdapter)
   * uses FirstOrDefault, so this matches that behavior.
   */
  getFirstByBooksellerId: async (booksellerId: string): Promise<BooksellerAuthConfigAdminDto | null> => {
    const { data } = await apiClient.get<AbpListResponse<BooksellerAuthConfigWithNavigationPropertiesDto>>(
      BASE,
      { params: { BooksellerId: booksellerId, MaxResultCount: 1, SkipCount: 0 } },
    );
    return data.items[0]?.booksellerAuthConfig ?? null;
  },

  create: async (input: CreateBooksellerAuthConfigDto): Promise<BooksellerAuthConfigAdminDto> => {
    const { data } = await apiClient.post<BooksellerAuthConfigAdminDto>(BASE, input);
    return data;
  },

  update: async (id: string, input: UpdateBooksellerAuthConfigDto): Promise<BooksellerAuthConfigAdminDto> => {
    const { data } = await apiClient.put<BooksellerAuthConfigAdminDto>(`${BASE}/${id}`, input);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
